import React, { useEffect, useMemo, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Card, CardContent, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Datagrid, FunctionField, ListContextProvider, NumberField, Pagination, TextField as RaTextField, Title, useList } from 'react-admin';
import { Bar, BarChart, Brush, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getAuthProviderFetchJson } from '../../lib/Auth';
import { superAdminApiServer } from '../../lib/config';

interface StatsBreakdown {
  internal: Record<string, number>;
  external: Record<string, number>;
}

interface CompanyStats {
  uuid: string;
  name: string;
  stats: StatsBreakdown;
}

interface TimeSlice {
  label: string;
  companies: CompanyStats[];
}

interface SliceData {
  slice: string;
  companies: CompanyStats[];
}

interface BreakdownResponse {
  from: string;
  to: string;
  slices: SliceData[];
}

interface FlatRow {
  id: string;
  uuid: string;
  name: string;
  endpoint: string;
  integrationType: 'internal' | 'external';
  count: number;
}

const todayHtml = () => new Date().toISOString().slice(0, 10);

// YYYY-MM-DD → YYYYMMDD0000 (start of day)
const dateToFromParam = (d: string) => d.replace(/-/g, '') + '0000';
// YYYY-MM-DD → YYYYMMDD2330 (last 30-min slot of day)
const dateToToParam = (d: string) => d.replace(/-/g, '') + '2330';

// Format slice key YYYYMMDDHHMM for display
const sliceLabel = (slice: string, singleDay: boolean) => {
  const h = slice.slice(8, 10);
  const m = slice.slice(10, 12);
  if (singleDay) return `${h}:${m}`;
  const mo = slice.slice(4, 6);
  const dy = slice.slice(6, 8);
  return `${mo}-${dy} ${h}:${m}`;
};

const useRangeData = (startDate: string, endDate: string, refreshKey: number): { timeSlices: TimeSlice[]; isLoading: boolean } => {
  const [timeSlices, setTimeSlices] = useState<TimeSlice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const singleDay = startDate === endDate;
    setIsLoading(true);
    const params = new URLSearchParams({ from: dateToFromParam(startDate), to: dateToToParam(endDate) });
    getAuthProviderFetchJson(`${superAdminApiServer}/api-counters/companies/breakdown?${params}`)
      .then(({ json }: any) => {
        const resp = json as BreakdownResponse;
        setTimeSlices(resp.slices.map(({ slice, companies }) => ({ label: sliceLabel(slice, singleDay), companies })));
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [startDate, endDate, refreshKey]);

  return { timeSlices, isLoading };
};

const SummaryCard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Card sx={{ textAlign: 'center', p: 1 }}>
    <Typography variant="h4" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Card>
);

const CHART_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0097a7', '#d32f2f'];

const CompanyBarChart: React.FC<{ data: { name: string; total: number }[] }> = ({ data }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
      Top Companies by Calls
    </Typography>
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 4, right: 16, bottom: 60, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="total" fill={CHART_COLORS[0]} />
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

const EndpointBarChart: React.FC<{ data: { name: string; total: number }[] }> = ({ data }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
      Top Endpoints by Calls
    </Typography>
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 4, right: 16, bottom: 60, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="total" fill={CHART_COLORS[1]} />
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

const TimeChart: React.FC<{
  data: { label: string; total: number }[];
  brushRange: { startIndex: number; endIndex: number } | null;
  onBrushChange: (range: { startIndex: number; endIndex: number }) => void;
}> = ({ data, brushRange, onBrushChange }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
      Calls per 30-min Period
    </Typography>
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 4, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Line type="monotone" dataKey="total" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
        <Brush
          dataKey="label"
          startIndex={brushRange?.startIndex ?? 0}
          endIndex={brushRange?.endIndex ?? Math.max(0, data.length - 1)}
          height={20}
          onChange={(b: any) => {
            if (b.startIndex !== undefined) onBrushChange({ startIndex: b.startIndex, endIndex: b.endIndex });
          }}
        />
      </LineChart>
    </ResponsiveContainer>
  </Box>
);

const sliceTotal = (cs: CompanyStats[]) => cs.reduce((s, c) => s + Object.values(c.stats.internal ?? {}).reduce((a, b) => a + b, 0) + Object.values(c.stats.external ?? {}).reduce((a, b) => a + b, 0), 0);

const ApiCountersDashboard: React.FC = () => {
  const [startDate, setStartDate] = useState(todayHtml);
  const [endDate, setEndDate] = useState(todayHtml);
  const [companyFilter, setCompanyFilter] = useState('');
  const [endpointFilter, setEndpointFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'internal' | 'external'>('all');
  const [activeTab, setActiveTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [brushRange, setBrushRange] = useState<{ startIndex: number; endIndex: number } | null>(null);

  useEffect(() => {
    setBrushRange(null);
  }, [startDate, endDate, refreshKey]);

  const { timeSlices, isLoading } = useRangeData(startDate, endDate, refreshKey);

  const filteredTimeSlices = useMemo<TimeSlice[]>(() => {
    if (!companyFilter && !endpointFilter && typeFilter === 'all') return timeSlices;
    const cf = companyFilter.toLowerCase();
    const ef = endpointFilter.toLowerCase();
    const filterKeys = (stats: Record<string, number>) => (ef ? Object.fromEntries(Object.entries(stats).filter(([ep]) => ep.toLowerCase().includes(ef))) : stats);
    return timeSlices.map(({ label, companies: cs }) => ({
      label,
      companies: cs
        .filter((c) => !cf || c.name.toLowerCase().includes(cf) || c.uuid.toLowerCase().includes(cf))
        .map((c) => ({
          ...c,
          stats: {
            internal: typeFilter === 'external' ? {} : filterKeys(c.stats.internal ?? {}),
            external: typeFilter === 'internal' ? {} : filterKeys(c.stats.external ?? {})
          }
        }))
        .filter((c) => Object.keys(c.stats.internal).length > 0 || Object.keys(c.stats.external).length > 0)
    }));
  }, [timeSlices, companyFilter, endpointFilter, typeFilter]);

  const activeSlices = useMemo(() => {
    if (!brushRange) return filteredTimeSlices;
    return filteredTimeSlices.slice(brushRange.startIndex, brushRange.endIndex + 1);
  }, [filteredTimeSlices, brushRange]);

  const companies = useMemo<CompanyStats[]>(() => {
    const map = new Map<string, CompanyStats>();
    for (const { companies: cs } of activeSlices) {
      for (const { uuid, name, stats } of cs) {
        if (!map.has(uuid)) map.set(uuid, { uuid, name, stats: { internal: {}, external: {} } });
        const entry = map.get(uuid)!;
        for (const type of ['internal', 'external'] as const) {
          for (const [ep, cnt] of Object.entries(stats[type] ?? {})) {
            entry.stats[type][ep] = (entry.stats[type][ep] ?? 0) + cnt;
          }
        }
      }
    }
    return Array.from(map.values());
  }, [activeSlices]);

  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    for (const { uuid, name, stats } of companies) {
      for (const type of ['internal', 'external'] as const) {
        for (const [endpoint, count] of Object.entries(stats[type] ?? {})) {
          rows.push({ id: `${uuid}:${type}:${endpoint}`, uuid, name, endpoint, integrationType: type, count });
        }
      }
    }
    return rows;
  }, [companies]);

  const companyTotals = useMemo(() => {
    const map: Record<string, { id: string; uuid: string; name: string; total: number }> = {};
    for (const r of flatRows) {
      if (!map[r.uuid]) map[r.uuid] = { id: r.uuid, uuid: r.uuid, name: r.name, total: 0 };
      map[r.uuid].total += r.count;
    }
    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .slice(0, 15);
  }, [flatRows]);

  const endpointTotals = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of flatRows) {
      const key = `${r.integrationType}: ${r.endpoint}`;
      map[key] = (map[key] ?? 0) + r.count;
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 15)
      .map(([key, total]) => {
        const sep = key.indexOf(': ');
        return { id: key, name: key.slice(sep + 2), integrationType: key.slice(0, sep), total };
      });
  }, [flatRows]);

  const totalCalls = useMemo(() => flatRows.reduce((s, r) => s + r.count, 0), [flatRows]);
  const activeCompanies = companies.length;
  const topEndpoint = endpointTotals[0]?.name ?? '—';

  const timeChartData = useMemo(() => filteredTimeSlices.map(({ label, companies: cs }) => ({ label, total: sliceTotal(cs) })), [filteredTimeSlices]);

  const timeTableRows = useMemo(
    () =>
      filteredTimeSlices.map(({ label, companies: cs }) => {
        const endpointMap: Record<string, number> = {};
        for (const { stats } of cs) {
          for (const type of ['internal', 'external'] as const) {
            for (const [ep, cnt] of Object.entries(stats[type] ?? {})) {
              endpointMap[ep] = (endpointMap[ep] ?? 0) + cnt;
            }
          }
        }
        const total = Object.values(endpointMap).reduce((a, b) => a + b, 0);
        const topEp = Object.entries(endpointMap).sort(([, a], [, b]) => b - a)[0]?.[0] ?? '—';
        return { id: label, label, total, topEndpoint: topEp };
      }),
    [filteredTimeSlices]
  );

  const brushStartLabel = filteredTimeSlices[brushRange?.startIndex ?? 0]?.label;
  const brushEndLabel = filteredTimeSlices[brushRange?.endIndex ?? filteredTimeSlices.length - 1]?.label;

  const companyListContext = useList({ data: companyTotals, isLoading, perPage: 25 });
  const endpointListContext = useList({ data: endpointTotals, isLoading, perPage: 25 });
  const allCallsListContext = useList({ data: flatRows, isLoading, perPage: 25 });
  const timeListContext = useList({ data: timeTableRows, isLoading, perPage: 50 });

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    if (endDate < val) setEndDate(val);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="API Usage Statistics" />

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField label="From" type="date" size="small" value={startDate} onChange={(e) => handleStartDateChange(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            <Typography sx={{ alignSelf: 'center' }}>–</Typography>
            <TextField label="To" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value < startDate ? startDate : e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: startDate }} sx={{ width: 160 }} />
            <TextField label="Filter by company name or UUID" size="small" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} sx={{ width: 260 }} />
            <TextField label="Filter by endpoint" size="small" value={endpointFilter} onChange={(e) => setEndpointFilter(e.target.value)} sx={{ width: 220 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'all' | 'internal' | 'external')}>
                <MenuItem value="all">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={() => setRefreshKey((k) => k + 1)}>
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Total Calls" value={totalCalls.toLocaleString()} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Active Companies" value={activeCompanies} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Top Endpoint" value={<Chip label={topEndpoint} size="small" sx={{ fontSize: 11, maxWidth: '100%', '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />} />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Top Companies" />
          <Tab label="Top Endpoints" />
          <Tab label="All Calls" />
          <Tab label="Time Breakdown" />
        </Tabs>

        <CardContent>
          {/* Top Companies tab */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <CompanyBarChart data={companyTotals} />
              </Box>
              <ListContextProvider value={companyListContext}>
                <Datagrid bulkActionButtons={false} sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}>
                  <RaTextField source="name" label="Company" />
                  <RaTextField source="uuid" label="UUID" />
                  <NumberField source="total" label="Calls" />
                </Datagrid>
                <Pagination rowsPerPageOptions={[10, 25, 50]} />
              </ListContextProvider>
            </Box>
          )}

          {/* Top Endpoints tab */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <EndpointBarChart data={endpointTotals} />
              </Box>
              <ListContextProvider value={endpointListContext}>
                <Datagrid bulkActionButtons={false} sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}>
                  <RaTextField source="name" label="Endpoint" />
                  <RaTextField source="integrationType" label="Type" />
                  <NumberField source="total" label="Calls" />
                </Datagrid>
                <Pagination rowsPerPageOptions={[10, 25, 50]} />
              </ListContextProvider>
            </Box>
          )}

          {/* All Calls tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                All Calls ({flatRows.length} rows)
              </Typography>
              <ListContextProvider value={allCallsListContext}>
                <Datagrid bulkActionButtons={false} sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}>
                  <RaTextField source="name" label="Company" />
                  <RaTextField source="uuid" label="UUID" />
                  <RaTextField source="endpoint" label="Endpoint" />
                  <RaTextField source="integrationType" label="Type" />
                  <NumberField source="count" label="Calls" />
                </Datagrid>
                <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
              </ListContextProvider>
            </Box>
          )}

          {/* Time Breakdown tab */}
          {activeTab === 3 && (
            <Box>
              <Box sx={{ mb: 1 }}>
                <TimeChart data={timeChartData} brushRange={brushRange} onBrushChange={setBrushRange} />
              </Box>
              {brushRange && brushStartLabel && brushEndLabel && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing: {brushStartLabel} – {brushEndLabel}
                  </Typography>
                  <Button size="small" onClick={() => setBrushRange(null)}>
                    Clear selection
                  </Button>
                </Box>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Calls by 30-min Period
              </Typography>
              <ListContextProvider value={timeListContext}>
                <Datagrid bulkActionButtons={false} sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}>
                  <FunctionField label="Period" render={(r: any) => r.label} />
                  <NumberField source="total" label="Total Calls" />
                  <RaTextField source="topEndpoint" label="Top Endpoint" />
                </Datagrid>
                <Pagination rowsPerPageOptions={[50, 100]} />
              </ListContextProvider>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ApiCountersDashboard;
